"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { fetchToken } from "@shared/src/config/firebase";
import { useNotificationStore } from "@shared/src/stores/useNotificationStore";

async function getNotificationPermissionAndToken() {
  if (!("Notification" in window)) {
    console.warn("This browser does not support push notifications.");
    return null;
  }

  if (Notification.permission === "granted") {
    try {
      return await fetchToken();
    } catch (error) {
      console.error("Error fetching FCM token:", error);
      return null;
    }
  }

  if (Notification.permission !== "denied") {
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        return await fetchToken();
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
    }
  }

  console.warn("Notification permission not granted.");
  return null;
}

const useFcmToken = () => {
  const [token, setToken] = useState<string | null>(null);
  const isLoading = useRef(false);
  const retryCount = useRef(0);

  const permissionStatus = useNotificationStore(state => state.permissionStatus);
  const setPermissionStatus = useNotificationStore(state => state.setPermissionStatus);

  useEffect(() => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      navigator.serviceWorker.getRegistration().then(existing => {
        if (!existing) {
          navigator.serviceWorker
            .register("/firebase-messaging-sw.js")
            .then(registration => {
              console.log("Service Worker registered:", registration);
            })
            .catch(error => {
              console.error("Service Worker registration failed:", error);
            });
        }
      });
    }
  }, []);

  const loadToken = useCallback(async () => {
    if (isLoading.current || token) return;
    isLoading.current = true;

    let fetchedToken: string | null = null;
    const maxRetries = 3;

    while (retryCount.current < maxRetries && !fetchedToken) {
      try {
        fetchedToken = await getNotificationPermissionAndToken();
        if (!fetchedToken) {
          console.warn(`Retry ${retryCount.current + 1}/${maxRetries}: Unable to fetch token.`);
          retryCount.current += 1;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error("Error fetching FCM token:", error);
      }
    }

    if (!fetchedToken) {
      console.warn("Push Notifications issue - unable to load token after max retries.");
      setPermissionStatus("denied");
    } else {
      console.log("Fetched FCM token:", fetchedToken);
      setPermissionStatus(Notification.permission);
      setToken(fetchedToken);
    }

    isLoading.current = false;
  }, [setPermissionStatus, token]);

  useEffect(() => {
    if ("Notification" in window && !token && permissionStatus !== "granted") {
      loadToken();
    }
  }, [loadToken, token, permissionStatus]);

  return { token, notificationPermissionStatus: permissionStatus };
};

export default useFcmToken;
